import { ChevronRight, Flag, Ban } from '../../components/ui/icons';
import { Sheet, Modal } from '../../components/ui/Sheet';
import type { ReportUserInput } from '../../lib/api';
import { REPORT_REASONS } from './constants';

export function ProfileSheets({
  name,
  isBlocked,
  menuOpen,
  reportOpen,
  blockOpen,
  reportPending,
  blockPending,
  onCloseMenu,
  onCloseReport,
  onCloseBlock,
  onOpenReport,
  onReport,
  onBlockOrUnblock,
  onConfirmBlock
}: {
  name: string;
  isBlocked: boolean;
  menuOpen: boolean;
  reportOpen: boolean;
  blockOpen: boolean;
  reportPending: boolean;
  blockPending: boolean;
  onCloseMenu: () => void;
  onCloseReport: () => void;
  onCloseBlock: () => void;
  onOpenReport: () => void;
  onReport: (reason: NonNullable<ReportUserInput['reason']>) => void;
  onBlockOrUnblock: () => void;
  onConfirmBlock: () => void;
}) {
  return (
    <>
      {/* overflow menu: report / block */}
      <Sheet open={menuOpen} onClose={onCloseMenu} ariaLabel="Player options" className="px-5 pb-7 pt-1">
        <div className="pb-2 pt-2 text-center text-[13px] font-bold tracking-snug text-ink2">{name}</div>
        <div className="flex flex-col gap-[10px]">
          <button
            type="button"
            onClick={onOpenReport}
            className="tap flex items-center gap-[13px] rounded-2xl border border-black/5 bg-card p-4 text-left shadow-tile"
          >
            <div className="grid h-[40px] w-[40px] shrink-0 place-items-center rounded-[12px] bg-amber-50 text-amber-600">
              <Flag width={19} height={19} strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-bold tracking-snug text-ink">Report player</div>
              <div className="text-[12px] font-medium text-ink2">Flag objectionable content or behaviour.</div>
            </div>
          </button>
          <button
            type="button"
            onClick={onBlockOrUnblock}
            className="tap flex items-center gap-[13px] rounded-2xl border border-black/5 bg-card p-4 text-left shadow-tile"
          >
            <div className="grid h-[40px] w-[40px] shrink-0 place-items-center rounded-[12px] bg-red-50 text-danger">
              <Ban width={19} height={19} strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-bold tracking-snug text-ink">
                {isBlocked ? 'Unblock player' : 'Block player'}
              </div>
              <div className="text-[12px] font-medium text-ink2">
                {isBlocked ? 'Allow friend requests and duels again.' : 'Stop all friend requests and duels.'}
              </div>
            </div>
          </button>
        </div>
      </Sheet>

      {/* report reason picker */}
      <Sheet open={reportOpen} onClose={onCloseReport} ariaLabel="Report player" className="px-5 pb-7 pt-1">
        <div className="pb-1 pt-2 text-center text-[16px] font-extrabold tracking-tight text-ink">Report {name}</div>
        <div className="pb-3 text-center text-[12px] font-medium text-ink2">
          Tell us what's wrong. Reports are confidential.
        </div>
        <div className="flex flex-col gap-[8px]">
          {REPORT_REASONS.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={reportPending}
              onClick={() => onReport(r.id)}
              className="tap flex items-center justify-between rounded-2xl border border-black/5 bg-card px-4 py-[14px] text-left text-[14px] font-semibold text-ink shadow-tile disabled:opacity-60"
            >
              {r.label}
              <ChevronRight width={17} height={17} strokeWidth={2.2} className="text-ink3" />
            </button>
          ))}
        </div>
      </Sheet>

      {/* block confirmation */}
      <Modal open={blockOpen} onClose={onCloseBlock} ariaLabel="Block player">
        <div className="grid h-[52px] w-[52px] place-items-center rounded-2xl bg-red-50 text-danger">
          <Ban width={26} height={26} strokeWidth={2.2} />
        </div>
        <h2 className="mt-4 text-[19px] font-extrabold tracking-tight text-ink">Block {name}?</h2>
        <p className="mt-2 text-[14px] font-medium leading-relaxed text-ink2">
          They won't be able to add you as a friend or challenge you to duels, and any existing friendship and
          pending duels will be removed. You can unblock them later.
        </p>
        <div className="mt-6 flex gap-[10px]">
          <button
            type="button"
            onClick={onCloseBlock}
            className="tap flex-1 rounded-[14px] border border-black/10 bg-card py-3 text-[14px] font-bold text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={blockPending}
            onClick={onConfirmBlock}
            className="tap flex-1 rounded-[14px] bg-danger py-3 text-[14px] font-extrabold text-white disabled:opacity-60"
          >
            Block
          </button>
        </div>
      </Modal>
    </>
  );
}
